/*
  ProMechBC — live search.

  Replaces Dawn's <predictive-search> in the header overlay. What it buys over
  the stock element, in order of how much a shopper feels it:

  1. IT ASKS FOR MORE THAN PRODUCTS. Dawn's element hard-codes nothing, but the
     stock markup only ever showed the resources Shopify returns by default.
     This asks for products, collections, articles, pages and Shopify's own
     query completions, so typing "apollo" surfaces the brand's shelf and the
     buying guide, not only eight scooters.

  2. IT ASKS MORE PRECISELY. `resources[options][fields]` restricts matching to
     the title, type, vendor and variant fields. Left at the default the engine
     also reads the product BODY, which is how a KUKIRIN scooter whose
     description compares itself to an Apollo came back as a result for
     "apollo". The same restriction is put on the form, so the results page
     agrees with the overlay.

  3. IT DOES NOT THRASH THE NETWORK. One in-flight request at a time, aborted
     the moment the next keystroke lands, and a short debounce — so a fast
     typist makes one request instead of eleven, and results can never arrive
     out of order and overwrite a newer answer with an older one.

  4. IT IS OPERABLE FROM THE KEYBOARD. Up/Down walk every result — rows and
     product cards alike, in DOM order — Enter follows the highlighted one,
     Escape steps back out. The combobox aria wiring is kept live so a screen
     reader is told how many results arrived and which one is active.

  5. IT REMEMBERS. Recent searches persist in localStorage and show alongside
     the merchandised chips when the field is empty, because the single most
     likely next search is the last one.

  Markup contract — the element owns these hooks and nothing else:
    <pmb-search>              wrapper, holds the form
      [data-ps-input]         the text field
      [data-ps-results]       injection target for the rendered section
      [data-ps-idle]          shown when the field is empty
      [data-ps-status]        visually hidden live region
      [data-ps-clear]         optional reset button
      [data-ps-recent]        optional container for remembered terms
*/
(function () {
  'use strict';

  var SECTION = 'pmb-predictive-search';
  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 220;
  var RECENT_KEY = 'pmb:recent-searches';
  var RECENT_MAX = 5;

  /* The fields the engine is allowed to match on. Kept in one place because
     the fetch and the form submission must agree — a results page that finds
     different things than the dropdown reads as a bug, not as a feature. */
  var DEFAULT_FIELDS = 'title,product_type,vendor,variants.title,variants.sku';

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (t) { return typeof t === 'string' && t.trim(); }) : [];
    } catch (e) {
      /* Private mode, a full quota, or a value someone else wrote. Search must
         still work without its history — never let storage take the feature
         down with it. */
      return [];
    }
  }

  function writeRecent(term) {
    term = (term || '').trim();
    if (term.length < MIN_CHARS) return;
    try {
      var list = readRecent().filter(function (t) {
        return t.toLowerCase() !== term.toLowerCase();
      });
      list.unshift(term);
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) { /* see readRecent */ }
  }

  function clearRecent() {
    try { window.localStorage.removeItem(RECENT_KEY); } catch (e) { /* see readRecent */ }
  }

  if (typeof HTMLElement === 'undefined' || !window.customElements) return;

  class PmbSearchElement extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-ps-input]');
      this.results = this.querySelector('[data-ps-results]');
      this.idle = this.querySelector('[data-ps-idle]');
      this.status = this.querySelector('[data-ps-status]');
      this.form = this.querySelector('form');
      if (!this.input || !this.results) return;

      this.fields = this.getAttribute('data-fields') || DEFAULT_FIELDS;
      this.limit = this.getAttribute('data-limit') || '5';
      this.controller = null;
      this.timer = null;
      this.activeIndex = -1;
      this.lastTerm = '';

      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKeydown);
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      var clear = this.querySelector('[data-ps-clear]');
      if (clear) {
        clear.addEventListener('click', function () {
          this.input.value = '';
          this.input.focus();
          this.onInput();
        }.bind(this));
      }

      var forget = this.querySelector('[data-ps-recent-clear]');
      if (forget) {
        forget.addEventListener('click', function () {
          clearRecent();
          this.renderRecent();
          this.input.focus();
        }.bind(this));
      }

      /* A result opened with the MOUSE is still a search worth remembering.
         Only Enter and form submission used to record one, so the shopper who
         clicks — most of them — never built up any history at all. */
      this.results.addEventListener('click', function (event) {
        if (event.target.closest('[data-ps-item]')) writeRecent(this.input.value);
      }.bind(this));

      this.renderRecent();
      this.reset();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      window.clearTimeout(this.timer);
    }

    /* ---- idle state ------------------------------------------------- */

    renderRecent() {
      var host = this.querySelector('[data-ps-recent]');
      if (!host) return;
      var list = readRecent();
      if (!list.length) {
        host.hidden = true;
        return;
      }
      host.hidden = false;
      var url = host.getAttribute('data-search-url') || '/search';
      var fields = this.fields || DEFAULT_FIELDS;
      /* Built as nodes rather than as an HTML string: the terms come out of
         localStorage, which anything on the origin can write to, and
         `textContent` cannot be talked into producing markup the way stripping
         a few characters out of an interpolated string can. */
      var frag = document.createDocumentFragment();
      list.forEach(function (term) {
        var a = document.createElement('a');
        a.className = 'chip';
        a.href = url + '?q=' + encodeURIComponent(term) +
          '&options%5Bprefix%5D=last&options%5Bfields%5D=' + encodeURIComponent(fields);
        a.textContent = term;
        frag.appendChild(a);
      });
      var listHost = host.querySelector('[data-ps-recent-list]');
      listHost.textContent = '';
      listHost.appendChild(frag);
    }

    reset() {
      if (this.controller) this.controller.abort();
      this.results.innerHTML = '';
      this.results.hidden = true;
      if (this.idle) this.idle.hidden = false;
      this.setLoading(false);
      this.setExpanded(false);
      this.activeIndex = -1;
    }

    /* ---- the query -------------------------------------------------- */

    onInput() {
      var term = this.input.value.trim();
      window.clearTimeout(this.timer);

      if (term.length < MIN_CHARS) {
        this.lastTerm = '';
        this.reset();
        return;
      }
      /* Re-typing the same term (a stray keystroke, a paste of what is already
         there) must not cost a round trip. */
      if (term === this.lastTerm) return;

      this.setLoading(true);
      this.timer = window.setTimeout(this.search.bind(this, term), DEBOUNCE_MS);
    }

    buildUrl(term) {
      var base = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      var params = [
        'q=' + encodeURIComponent(term),
        'resources[type]=product,collection,article,page,query',
        'resources[limit]=' + encodeURIComponent(this.limit),
        'resources[limit_scope]=each',
        'resources[options][unavailable_products]=last',
        'resources[options][fields]=' + encodeURIComponent(this.fields),
        'section_id=' + SECTION
      ];
      return base + (base.indexOf('?') > -1 ? '&' : '?') + params.join('&');
    }

    search(term) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      var signal = this.controller.signal;

      fetch(this.buildUrl(term), { signal: signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Search failed: ' + res.status);
          return res.text();
        })
        .then(function (html) {
          if (signal.aborted) return;
          this.lastTerm = term;
          this.paint(html);
        }.bind(this))
        .catch(function (err) {
          /* An abort is the normal path, not a failure: it fires every time
             the shopper keeps typing. Anything else leaves the dropdown as it
             was and lets the form still submit — a broken suggestion list must
             never block an actual search. */
          if (err && err.name === 'AbortError') return;
          this.setLoading(false);
        }.bind(this));
    }

    paint(html) {
      /* The Section Rendering API answers with the whole section wrapper. Only
         the results node is wanted, and reading it out of a detached document
         means no half-parsed markup is ever in the live DOM. */
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var node = doc.querySelector('#predictive-search-results');

      this.setLoading(false);
      /* No results node means the section did not render — an app that broke
         the endpoint, a 200 that is really an error page. Falling through
         would swap the idle panel for an empty white box; going back to idle
         at least leaves the shopper the categories and the chips. */
      if (!node) {
        this.reset();
        return;
      }

      if (this.idle) this.idle.hidden = true;
      this.results.hidden = false;
      this.results.innerHTML = node.outerHTML;
      this.restoreTerm();
      this.activeIndex = -1;

      /* aria-activedescendant points at an id, so every option needs one. They
         are stamped here rather than in Liquid because the ids only have to be
         unique within this one freshly-painted list. */
      this.items().forEach(function (el, i) {
        el.id = 'pmb-ps-option-' + i;
      });
      this.setExpanded(true);

      var countNode = doc.querySelector('[data-ps-count]');
      var count = countNode ? parseInt(countNode.textContent, 10) : this.items().length;
      this.announce(count);
    }

    /* The suggest endpoint clips a long query before echoing it back, so the
       footer row can offer to "see all results" for a term the shopper never
       typed — and link to that clipped term as well. The live field is the
       authority on what was asked for. */
    restoreTerm() {
      var all = this.results.querySelector('[data-ps-all]');
      if (!all) return;
      var typed = this.input.value.trim();
      var echoed = all.getAttribute('data-ps-terms') || '';
      if (!typed || typed === echoed) return;

      all.setAttribute('href', all.getAttribute('href').replace(/([?&]q=)[^&]*/, '$1' + encodeURIComponent(typed)));
      var label = all.querySelector('[data-ps-all-label]');
      if (label) label.textContent = 'See all results for “' + typed + '”';
    }

    /* ---- keyboard --------------------------------------------------- */

    items() {
      return Array.prototype.slice.call(this.results.querySelectorAll('[data-ps-item]'));
    }

    onKeydown(event) {
      var items = this.items();

      if (event.key === 'Escape') {
        /* Escape steps OUT one level at a time: it dismisses the result list
           first and only closes the whole overlay on a second press. The
           overlay's own Escape handler lives on the document (pmb-site.js),
           so this one has to stop the event once it has consumed it. */
        if (this.results.hidden) return;
        event.stopPropagation();
        this.reset();
        this.lastTerm = '';
        return;
      }

      if (event.key === 'Enter') {
        var active = items[this.activeIndex];
        if (active) {
          event.preventDefault();
          writeRecent(this.input.value);
          window.location.href = active.href;
        }
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!items.length) return;
      event.preventDefault();

      /* Wrapping is deliberate: with a short list, walking off the end and
         landing back at the top is faster than reversing direction. */
      var next = event.key === 'ArrowDown' ? this.activeIndex + 1 : this.activeIndex - 1;
      if (next >= items.length) next = 0;
      if (next < 0) next = items.length - 1;
      this.highlight(next, items);
    }

    highlight(index, items) {
      items = items || this.items();
      items.forEach(function (el, i) {
        var on = i === index;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.activeIndex = index;

      var el = items[index];
      if (!el) return;
      this.input.setAttribute('aria-activedescendant', el.id || '');
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }

    onSubmit(event) {
      var term = this.input.value.trim();
      if (!term) {
        event.preventDefault();
        return;
      }
      writeRecent(term);
    }

    /* ---- state ------------------------------------------------------ */

    setLoading(on) {
      this.classList.toggle('is-loading', !!on);
      this.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    setExpanded(on) {
      this.input.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (!on) this.input.removeAttribute('aria-activedescendant');
    }

    announce(count) {
      if (!this.status) return;
      this.status.textContent = count === 0
        ? 'No results found'
        : count + (count === 1 ? ' result available' : ' results available') +
          '. Use up and down arrow keys to review, enter to open.';
    }
  }

  if (!window.customElements.get('pmb-search')) {
    window.customElements.define('pmb-search', PmbSearchElement);
  }

  /* Exposed so anything else that builds a search URL shares one definition of
     "which fields count" rather than keeping a second copy that drifts. */
  window.PmbSearchFields = DEFAULT_FIELDS;
})();
